import { Box } from '@mui/material';
import type { NotaPublicaCard } from '@ahora-patagonia/contracts';
import { TarjetaNota } from '../../../shared/notas/TarjetaNota.js';

export interface GrillaDeTarjetasProps {
  notas: NotaPublicaCard[];
  /** Cuántas columnas en cada corte: teléfono (`xs`), tablet (`sm`) y escritorio (`lg`). */
  columnas: { xs: number; sm: number; lg: number };
  /** `sizes` de la foto: cambia con las columnas, si no la variante que se pide no es la que se ve. */
  sizes: string;
  /** Posición numerada sobre la foto: la disposición con la que se dibuja "Más leídas". */
  numerada?: boolean;
  /** Con qué número arranca (la segunda página del ranking empieza en 5, no en 1). */
  inicio?: number;
}

/**
 * La grilla de tarjetas con foto arriba y título abajo, que es la unidad que comparten las dos
 * disposiciones que dibujan tarjetas: la **tira** (plantilla B, 4 por fila) y la **cuadrícula**
 * (plantilla D, 3 por fila y sin paginar).
 *
 * Existe para que esas dos no sean el mismo archivo copiado: lo único que las distingue es cuántas
 * columnas tienen, cuántas notas dejan entrar y qué `sizes` piden. Lo demás —el `<ol>` de la
 * numeración, dónde va cada número, el gap— es idéntico, y es justo la parte que no se puede
 * duplicar sin que una de las dos se quede atrás.
 *
 * Numerada se dibuja como **lista ordenada**, por lo mismo que en la plantilla C: el número sobre la
 * foto va `aria-hidden`, y la posición en un ranking no puede existir solo para el que ve.
 */
export function GrillaDeTarjetas({
  notas,
  columnas,
  sizes,
  numerada = false,
  inicio = 1,
}: GrillaDeTarjetasProps) {
  return (
    <Box
      component={numerada ? 'ol' : 'div'}
      {...(numerada ? { start: inicio } : {})}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: `repeat(${columnas.xs}, minmax(0, 1fr))`,
          sm: `repeat(${columnas.sm}, minmax(0, 1fr))`,
          lg: `repeat(${columnas.lg}, minmax(0, 1fr))`,
        },
        gap: 2.5,
        ...(numerada ? { listStyle: 'none', m: 0, p: 0 } : {}),
      }}
    >
      {notas.map((nota, i) =>
        numerada ? (
          <Box component="li" key={nota.id}>
            <TarjetaNota nota={nota} sizes={sizes} numero={inicio + i} />
          </Box>
        ) : (
          <TarjetaNota key={nota.id} nota={nota} sizes={sizes} />
        ),
      )}
    </Box>
  );
}
